import React, { useEffect, useState } from "react";

import { apiCall } from '@app/utils/apiCall';
import { useAuth } from '@app/contexts/AuthContext';
import moment from 'moment';
import { formatNumberDisplay } from "@app/utils/util";

/**
 * Display structure data CSPE ventilation in a dedicated table
 * @returns 
 */
export default function CspeVentilation({structureDataId, loaded}) {
    const [cspeRecords, setCspeRecords] = useState([]);

    const auth = useAuth();
    
    if (!loaded && structureDataId) {
        apiCall({
            method: "get",
            path: `/api/v1/cspe_records/show_cspe_ventilation?structure_data_id=${structureDataId}`,
            auth: auth,
            onSuccess: (response) => {
                // setLoaded(true);
                setCspeRecords(response.data.cspe_records);
            }
        });
    }
    
    function showCspeRecord(cspeRecord, index) {
        const startDate = moment(cspeRecord.start_date).format("DD/MM/YYYY");
        const endDate = moment(cspeRecord.end_date).format("DD/MM/YYYY");

        const regul = cspeRecord.cspe_regul.toFixed(2);        
        const regulClass = regul >= 0 ? "error-bold-text" : "primary-bold-text";

        return (
            <tr key={index}>
                <td>
                    <b>{startDate}</b>
                    <br/>
                    {endDate}
                </td>
                <td>{cspeRecord.value.toFixed(2)} €/MWh</td>
                <td>{cspeRecord.cspe_tax_reduced_rate.toFixed(2)} €/MWh</td>
                <td>{formatNumberDisplay(cspeRecord.kWh_value)} kWh</td>
                <td><b>{cspeRecord.cspe_paid.toFixed(2)} €</b></td>
                <td>{cspeRecord.cspe_value.toFixed(2)} €</td>
                <td><span className={regulClass}>{regul} €</span></td>
            </tr>
        )
    }
    const { 
        cspeAggregated, totalCspePaid, 
        totalCspeRegul, totalCspeValue, totalKwh 
    } = aggregateByValue(cspeRecords);
    
    function showTotals() {
        return (
            <tr className="table-info">
                <td colSpan="3"><b>Total</b></td>
                <td><b>{formatNumberDisplay(totalKwh)} kWh</b></td>
                <td><b>{totalCspePaid.toFixed(2)} €</b></td>
                <td><b>{totalCspeValue.toFixed(2)} €</b></td>
                <td><span className="primary-bold-text">{totalCspeRegul.toFixed(2)} €</span></td>
            </tr>
        )
    }

    return (
        <div>
            <hr />
                <table className="custom-table">
                    <thead>
                        <tr>
                            <th key="1">
                                Période
                            </th>
                            <th key="2">
                                Taux appliqué
                            </th>
                            <th key="3">
                                Taux réduit
                            </th>
                            <th key="4">
                                Quantité
                            </th>
                            <th key="5">
                                Montant CSPE Payé
                            </th>
                            <th key="6">
                                Montant CSPE <br/>
                                au tarif réduit applicable
                            </th>
                            <th key="7">
                                Régularisation
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {cspeAggregated.map(showCspeRecord)}
                        {showTotals()}
                    </tbody>
                </table>
        </div>
    )
}

/**
 * aggregate the cspe records depending on their applied values
 * 
 */
function aggregateByValue(cspeRecords) {
    const groupedByValue = {};

    // SUM VALUES
    let totalKwh = 0;
    let totalCspePaid = 0;
    let totalCspeValue = 0;
    let totalCspeRegul = 0;

    cspeRecords.forEach((record) => { 
        // Get applied default value   
        const valueKey = record.value;

        // Init the new object if not existing
        if (!groupedByValue[valueKey]) {
            groupedByValue[valueKey] = {
                value: valueKey,
                cspe_paid: 0,
                cspe_regul: 0,
                cspe_value: 0,
                kWh_value: 0,
                start_date: record.start_date,
                end_date: record.end_date,
                cspe_tax_reduced_rate: record.cspe_tax_reduced_rate,
                count: 0,
            };
        }

        // Extract the values
        const cspePaid = record.cspe_paid ?? 0;
        const cspeRegul = record.cspe_regul ?? 0;
        const cspeValue = record.cspe_value ?? 0;
        const kWhValue = record.kWh_value ?? 0;

        // Sum the totals
        totalCspePaid += cspePaid;
        totalCspeRegul += cspeRegul;
        totalCspeValue += cspeValue;
        totalKwh += kWhValue;

        // Sum the values
        groupedByValue[valueKey].cspe_paid += cspePaid;
        groupedByValue[valueKey].cspe_regul += cspeRegul;
        groupedByValue[valueKey].cspe_value += cspeValue;
        groupedByValue[valueKey].kWh_value += kWhValue;
        groupedByValue[valueKey].count += 1;

        // Update the dates 
        const currentStartDate = new Date(groupedByValue[valueKey].start_date);
        const newStartDate = new Date(record.start_date);
        
        if (newStartDate < currentStartDate) {
            groupedByValue[valueKey].start_date = record.start_date;
        }

        const currentEndDate = new Date(groupedByValue[valueKey].end_date);
        const newEndDate = new Date(record.end_date);
        
        if (newEndDate > currentEndDate) {
            groupedByValue[valueKey].end_date = record.end_date;
        }
    });

    return {
        cspeAggregated: Object.values(groupedByValue),
        totalCspePaid: totalCspePaid,
        totalCspeRegul: totalCspeRegul,
        totalCspeValue: totalCspeValue,
        totalKwh: totalKwh
    };
}